import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CATEGORIES, SERVICES } from "@/data/services";

const slug = (c) => c.toLowerCase().replace(/[^a-z]+/g, "-");

const Services = () => {
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? SERVICES : SERVICES.filter((s) => s.category === active);

  return (
    <div data-testid="services-page" className="pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Services & technologies</Eyebrow>
          <h1 className="mt-4 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            A full-spectrum <span className="text-gradient">green technology</span> catalogue
          </h1>
          <p className="mt-6 max-w-2xl text-gray-300 text-base sm:text-lg leading-relaxed">
            Consultancy from concept to creation, installation, commissioning and operation —
            across {SERVICES.length} specialized plant technologies. Open any technology for its
            process flow and typical configuration.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label="Service categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                role="tab"
                aria-selected={active === c}
                data-testid={`service-filter-${slug(c)}`}
                onClick={() => setActive(c)}
                className={`rounded-full px-4 py-2 text-sm font-medium border transition-all duration-300 ${
                  active === c
                    ? "bg-lime text-obsidian border-lime"
                    : "border-white/15 text-gray-300 hover:border-lime/50 hover:text-lime"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((s) => (
              <motion.article
                layout
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`service-card-${s.id}`}
                className="group rounded-2xl border border-white/10 bg-carbon p-7 hover:border-lime/50 hover:bg-steel hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(132,204,22,0.12)] transition-all duration-300 flex flex-col"
              >
                <div className="flex items-start justify-between">
                  <s.icon className="text-lime" size={26} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 border border-white/10 rounded-full px-3 py-1">
                    {s.category}
                  </span>
                </div>
                <h2 className="mt-6 font-display font-bold text-xl text-white group-hover:text-lime transition-colors">
                  {s.title}
                </h2>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{s.tagline}</p>
                <ul className="mt-4 space-y-1.5 text-sm text-gray-400">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-2">
                      <span className="mt-2 h-1 w-1 rounded-full bg-lime shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-5 flex items-center justify-between">
                  <Link
                    to={`/services/${s.id}`}
                    data-testid={`service-details-${s.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-lime hover:gap-2.5 transition-all duration-300"
                  >
                    View details <ArrowRight size={15} />
                  </Link>
                  <Link
                    to={`/contact?service=${s.id}`}
                    data-testid={`service-enquire-${s.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-lime hover:gap-2.5 transition-all duration-300"
                  >
                    Enquire <ArrowUpRight size={15} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;
